import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  capitalize,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Skeleton,
  Typography,
} from '@mui/material'
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import {
  ExpandIcon,
  PreviewIcon,
  CorrectIcon,
  CancelIcon,
  SubmitIcon,
  VerifyIcon,
} from 'src/v3/core/icons'
import axios, { pluckData } from 'src/utilities/http-client'
import { ComplianceItemProgress, ComplianceProgress } from 'src/v3/core/types'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { LightweightUser } from '../../manage.slice'
import { useNotifications } from '../../hooks/use-notifications'
import {
  fetchComplianceProgress,
  fetchComplianceItemUsers,
  selectComplianceItemUsers,
  selectComplianceItemUsersStatus,
} from '../../compliance.slice'
import { dataGridSx } from '../../styles'
import { sortComplianceItems } from '../types'
import RecertCycleDetailModal from './modals/recert-cycle-detail-modal'
import { DocumentPreviewDialog } from './document-preview-dialog'

type Props = {
  complianceID: string
  linkedServiceID: string | undefined
  progress: ComplianceProgress | null
  users: LightweightUser[]
  isLoading: boolean
}

type ItemContentProps = {
  item: ComplianceItemProgress
  complianceID: string
  users: LightweightUser[]
  linkedServiceID: string | undefined
  onProgressRefresh: () => void
}

function SkeletonCard() {
  return (
    <Card className="p-6 mb-6">
      <Skeleton variant="text" width={220} height={28} className="mb-4" />
      {[0, 1, 2].map((i) => (
        <Skeleton
          key={i}
          variant="rectangular"
          height={56}
          sx={{ borderRadius: 1, mb: 1 }}
        />
      ))}
    </Card>
  )
}

function ItemAccordionContent({
  item,
  complianceID,
  linkedServiceID,
  users,
  onProgressRefresh,
}: ItemContentProps) {
  let dispatch = useAppDispatch()
  let userJoins =
    useAppSelector(
      selectComplianceItemUsers(complianceID, item.complianceItemID),
    ) ?? []
  let joinsStatus = useAppSelector(
    selectComplianceItemUsersStatus(complianceID, item.complianceItemID),
  )
  let loadingJoins = joinsStatus === 'pending' || joinsStatus === undefined
  let [viewBlob, setViewBlob] = useState<Blob | null>(null)
  // The presigned URL's path ends in the stored file name, used to detect PDFs
  // served without a usable content type.
  let [viewFileName, setViewFileName] = useState<string>()
  let [loadingPreview, setLoadingPreview] = useState<string | null>(null)
  let [verifyTarget, setVerifyTarget] = useState<{
    userID: string
    entityID: string
  } | null>(null)
  let [verifyDecision, setVerifyDecision] = useState<'accepted' | 'rejected'>(
    'accepted',
  )
  let [saving, setSaving] = useState(false)
  let [detailCycleID, setDetailCycleID] = useState<string | null>(null)
  let [detailCycleName, setDetailCycleName] = useState<string | null>(null)

  let { showError, NotificationComponent } = useNotifications()

  let userMap = useMemo(() => new Map(users.map((u) => [u.id, u])), [users])

  let refreshUserJoins = useCallback(() => {
    dispatch(
      fetchComplianceItemUsers({ complianceID, itemID: item.complianceItemID }),
    )
  }, [dispatch, complianceID, item.complianceItemID])

  useEffect(() => {
    if (!joinsStatus || joinsStatus === 'uninitialized') {
      refreshUserJoins()
    }
  }, [joinsStatus, refreshUserJoins])

  let handlePreview = useCallback(
    (entityID: string) => {
      let imageUrl =
        item.itemType === 'credential'
          ? `/api/v2/manage/credential/image-url?CredentialID=${entityID}`
          : `/api/manage/${item.itemType}/image/${entityID}`
      setLoadingPreview(entityID)
      axios
        .get<string>(imageUrl)
        .then(pluckData)
        .then((url) => {
          setViewFileName(url.split('?')[0])
          return axios.get<Blob>(url, { responseType: 'blob' })
        })
        .then(pluckData)
        .then((blob) => setViewBlob(blob))
        .catch((err) =>
          showError(
            err?.response?.data || err?.message || 'No image available',
          ),
        )
        .finally(() => setLoadingPreview(null))
    },
    [item.itemType, showError],
  )

  let handleSubmitVerify = useCallback(() => {
    if (!verifyTarget || !linkedServiceID) return
    setSaving(true)
    let endpoint =
      item.itemType === 'license'
        ? '/api/manage/license/compliance-verify'
        : item.itemType === 'nremt'
          ? '/api/manage/nremt/compliance-verify'
          : '/api/manage/credential/compliance-verify'
    let payload =
      item.itemType === 'license'
        ? {
            licenseID: verifyTarget.entityID,
            userID: verifyTarget.userID,
            linkedServiceID,
            status: verifyDecision,
          }
        : item.itemType === 'nremt'
          ? {
              userID: verifyTarget.userID,
              linkedServiceID,
              status: verifyDecision,
            }
          : {
              credentialID: verifyTarget.entityID,
              linkedServiceID,
              status: verifyDecision,
            }
    axios
      .post(endpoint, payload)
      .then(() => {
        setVerifyTarget(null)
        setVerifyDecision('accepted')
        onProgressRefresh()
        refreshUserJoins()
      })
      .catch((err) =>
        showError(err?.response?.data || err?.message || 'Verification failed'),
      )
      .finally(() => setSaving(false))
  }, [
    verifyTarget,
    verifyDecision,
    item.itemType,
    linkedServiceID,
    onProgressRefresh,
    refreshUserJoins,
    showError,
  ])

  let rows = useMemo(
    () =>
      userJoins.map((join) => {
        let user = userMap.get(join.userID)
        return {
          id: join.userID,
          userID: join.userID,
          name: user ? `${user.firstName} ${user.lastName}` : join.userID,
          email: user?.primaryEmail ?? '',
          status: join.status,
          entityID: join.entityID,
          entityLabel: join.entityLabel,
        }
      }),
    [userJoins, userMap],
  )

  let columns = useMemo<GridColDef[]>(() => {
    let base: GridColDef[] = [
      { field: 'name', headerName: 'User', flex: 1, minWidth: 160 },
      { field: 'email', headerName: 'Email', flex: 1, minWidth: 180 },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        renderCell: (params) => {
          let value = params.value as string | null
          let displayValue = value === 'started' ? 'pending' : value
          let colorMap: Record<
            string,
            'success' | 'error' | 'warning' | 'primary' | 'default'
          > = {
            accepted: 'success',
            completed: 'success',
            rejected: 'error',
            pending: 'warning',
          }
          return (
            <Box className="flex items-center h-full">
              <Chip
                label={displayValue ? capitalize(displayValue) : 'None'}
                color={
                  displayValue
                    ? (colorMap[displayValue] ?? 'default')
                    : 'default'
                }
                size="small"
              />
            </Box>
          )
        },
      },
    ]

    if (
      item.itemType === 'license' ||
      item.itemType === 'credential' ||
      item.itemType === 'nremt'
    ) {
      // eslint-disable-next-line fp/no-mutating-methods
      base.push({
        field: 'actions',
        headerName: 'Actions',
        width: 200,
        sortable: false,
        renderCell: (params) => {
          let { userID, entityID, status } = params.row as {
            userID: string
            entityID: string | null
            status: string | null
          }
          let isCompleted = status === 'accepted' || status === 'completed'

          if (!entityID) {
            return (
              <Box className="flex items-center h-full">
                <Typography variant="body2" color="text.secondary">
                  No {item.itemType === 'nremt' ? 'NREMT' : item.itemType}{' '}
                  linked
                </Typography>
              </Box>
            )
          }

          return (
            <Box className="flex items-center h-full gap-2">
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  loadingPreview === entityID ? (
                    <CircularProgress size={14} />
                  ) : (
                    <PreviewIcon />
                  )
                }
                disabled={loadingPreview === entityID}
                onClick={() => handlePreview(entityID)}
              >
                View
              </Button>
              {isCompleted ? (
                <Box className="flex items-center gap-1">
                  <CorrectIcon sx={{ fontSize: 16, color: 'success.main' }} />
                  <Typography variant="body2" sx={{ color: 'success.main' }}>
                    Verified
                  </Typography>
                </Box>
              ) : (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<VerifyIcon />}
                  onClick={() => setVerifyTarget({ userID, entityID })}
                >
                  Review
                </Button>
              )}
            </Box>
          )
        },
      })
    } else if (item.itemType === 'recert') {
      // eslint-disable-next-line fp/no-mutating-methods
      base.push(
        {
          field: 'entityLabel',
          headerName: 'Cycle Name',
          flex: 1,
          minWidth: 160,
          renderCell: (params) => (
            <Box className="flex items-center h-full">
              <Typography
                variant="body2"
                color={params.value ? 'text.primary' : 'text.secondary'}
              >
                {params.value ?? '—'}
              </Typography>
            </Box>
          ),
        },
        {
          field: 'cycleDetail',
          headerName: 'Cycle Details',
          width: 120,
          sortable: false,
          renderCell: (params) => {
            let { entityID, entityLabel } = params.row as {
              entityID: string | null
              entityLabel: string | null
            }
            if (!entityID) return null
            return (
              <Box className="flex items-center h-full">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setDetailCycleID(entityID)
                    setDetailCycleName(entityLabel)
                  }}
                >
                  Detail
                </Button>
              </Box>
            )
          },
        },
      )
    }

    return base
  }, [item.itemType, loadingPreview, handlePreview])

  return (
    <>
      <DataGridPremium
        rows={rows}
        columns={columns}
        loading={loadingJoins}
        autoHeight
        disableRowSelectionOnClick
        pagination
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        sx={dataGridSx}
      />

      <DocumentPreviewDialog
        blob={viewBlob}
        fileName={viewFileName}
        onClose={() => setViewBlob(null)}
      />

      <Dialog
        open={!!verifyTarget}
        onClose={() => {
          setVerifyTarget(null)
          setVerifyDecision('accepted')
        }}
      >
        <Paper sx={{ width: 300, px: 2, py: 1 }}>
          <DialogTitle sx={{ textAlign: 'center', mb: -1 }}>
            Verify{' '}
            {item.itemType === 'nremt' ? 'NREMT' : capitalize(item.itemType)}
          </DialogTitle>
          <DialogContent
            sx={{
              px: 2,
              pb: 1,
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
            }}
          >
            <FormControl>
              <Typography color="textSecondary" sx={{ ml: -1, my: 0.5 }}>
                Decision
              </Typography>
              <RadioGroup
                value={verifyDecision}
                onChange={(e) =>
                  setVerifyDecision(e.target.value as 'accepted' | 'rejected')
                }
              >
                <FormControlLabel
                  value="accepted"
                  control={<Radio sx={{ height: 32, width: 32 }} />}
                  label="Accept"
                />
                <FormControlLabel
                  value="rejected"
                  control={<Radio sx={{ height: 32, width: 32 }} />}
                  label="Reject"
                />
              </RadioGroup>
            </FormControl>
          </DialogContent>
          <DialogActions
            sx={{ display: 'flex', justifyContent: 'center', pb: 1.5 }}
          >
            <Button
              color="primary"
              variant="contained"
              sx={{ flex: '1 1 0px' }}
              startIcon={<SubmitIcon />}
              loading={saving}
              onClick={handleSubmitVerify}
            >
              Submit
            </Button>
            <Button
              color="error"
              variant="contained"
              sx={{ flex: '1 1 0px' }}
              startIcon={<CancelIcon />}
              disabled={saving}
              onClick={() => {
                setVerifyTarget(null)
                setVerifyDecision('accepted')
              }}
            >
              Cancel
            </Button>
          </DialogActions>
        </Paper>
      </Dialog>

      <RecertCycleDetailModal
        cycleID={detailCycleID}
        cycleName={detailCycleName}
        onClose={() => {
          setDetailCycleID(null)
          setDetailCycleName(null)
        }}
      />

      {NotificationComponent}
    </>
  )
}

export default function RequirementsAccordionCard({
  complianceID,
  linkedServiceID,
  progress,
  users,
  isLoading,
}: Props) {
  let dispatch = useAppDispatch()
  let [expanded, setExpanded] = useState<string | false>(false)

  if (isLoading || !progress) return <SkeletonCard />

  let handleChange =
    (itemID: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? itemID : false)
    }

  let handleProgressRefresh = () => {
    dispatch(fetchComplianceProgress(complianceID))
  }

  let sortedItems = sortComplianceItems(progress.items)

  return (
    <Card className="p-6 mb-6">
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }} className="mb-4">
        Progress by Requirement
      </Typography>

      {sortedItems.map((item) => {
        let completionRate =
          item.totalUsers > 0
            ? Math.round((item.completedUsers / item.totalUsers) * 100)
            : 0

        return (
          <Accordion
            key={item.complianceItemID}
            expanded={expanded === item.complianceItemID}
            onChange={handleChange(item.complianceItemID)}
            disableGutters
            sx={{
              mb: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '4px !important',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandIcon />} sx={{ px: 2 }}>
              <Box className="flex flex-wrap items-center flex-1 gap-3 mr-2">
                <Chip
                  label={
                    item.itemType === 'recert'
                      ? 'Recertification'
                      : item.itemType === 'nremt'
                        ? 'NREMT'
                        : capitalize(item.itemType)
                  }
                  variant="outlined"
                  size="small"
                  sx={{ flexShrink: 0 }}
                />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, flex: 1, minWidth: 160 }}
                >
                  {item.label}
                </Typography>
                <Chip
                  label={`${item.completedUsers}/${item.totalUsers} Complete`}
                  color={completionRate === 100 ? 'success' : 'primary'}
                  size="small"
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {expanded === item.complianceItemID && (
                <ItemAccordionContent
                  item={item}
                  complianceID={complianceID}
                  users={users}
                  linkedServiceID={linkedServiceID}
                  onProgressRefresh={handleProgressRefresh}
                />
              )}
            </AccordionDetails>
          </Accordion>
        )
      })}

      {progress.items.length === 0 && (
        <Typography variant="body2" color="text.secondary" className="mt-2">
          No requirements found.
        </Typography>
      )}
    </Card>
  )
}
